import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { createColumnHelper } from '@tanstack/react-table';
import { Button, InlineNotification } from '@carbon/react';
import { MapBoundary } from '@carbon/react/icons';
import { FloatingTooltip } from 'components/floating-tooltip/floating-tooltip';
import { formatCellValue } from 'components/data-table/utils';
import { Link, PlainLink } from 'components/link/link';
import { EMPTY_CELL, LinkCell } from 'components/link-cell';
import { DataTable, type DataTableColumnDef } from 'components/data-table';
import type {
  InitialTableState,
  TableState,
} from 'components/data-table/types';
import { sessionResourceAtom } from 'atoms/root';
import { subscribe } from 'lib/pusher';
import { AfeCatalogFilters } from './afe-catalog-filters';
import { AfeCatalogMap } from './afe-catalog-map';
import { AfeVerificationIndicator } from './afe-verification-indicator';
import { SummaryCards } from './summary-cards';
import {
  createAfeCatalogSearchAtom,
  exportAfeCatalogAtom,
  type AfeCatalogParams,
} from './atoms';
import type { FlatAfeRow } from './types';
import {
  DEFAULT_PARAMS,
  flattenResults,
  WELL_TYPE_LABELS,
  AFE_TYPE_LABELS,
  columnFiltersToParams,
} from './utils';
import { CollapsedList } from './collapsed-list';
import styles from './afe-catalog-content.module.scss';

// ─────────────────────────────────────
// Column Definitions
// ─────────────────────────────────────

type AfeRow = FlatAfeRow & Record<string, unknown>;

const columnHelper = createColumnHelper<FlatAfeRow>();

/** 24 column definitions for the AFE Catalog DataTable. */
export const columns = [
  // --- Pinned columns (1-4) ---
  columnHelper.accessor('id', {
    id: 'id',
    header: 'AFE ID',
    size: 90,
    cell: (info) => {
      const value = info.getValue();
      return <Link to={`/afe_catalog/${value}`}>{value}</Link>;
    },
  }),

  columnHelper.accessor('wellName', {
    id: 'wellName',
    header: 'Well Name',
    size: 180,
  }),

  columnHelper.accessor('verificationState', {
    id: 'verificationState',
    header: 'Verified',
    size: 130,
    cell: (info) => <AfeVerificationIndicator state={info.getValue()} />,
  }),

  columnHelper.accessor('slotId', {
    id: 'slotId',
    header: 'UWI',
    size: 140,
    cell: (info) => {
      const value = info.getValue();
      const href = info.row.original.wellHref;
      if (!value) return EMPTY_CELL;
      return <Link to={href ?? `/wells/${value}`}>{value}</Link>;
    },
  }),

  columnHelper.accessor('senders', {
    id: 'senderEntityName',
    header: 'Sender Entity',
    size: 200,
    cell: (info) => (
      <CollapsedList
        items={info.getValue()}
        getLabel={(s) => s.name}
        renderItem={(s) => <Link to={`/entities/${s.id}`}>{s.name}</Link>}
      />
    ),
  }),

  // --- Unpinned columns (5-23) ---
  columnHelper.accessor('receivers', {
    id: 'receiverEntityName',
    header: 'Receiver Entity',
    size: 200,
    cell: (info) => (
      <CollapsedList
        items={info.getValue()}
        getLabel={(r) => r.name}
        renderItem={(r) => <Link to={`/entities/${r.id}`}>{r.name}</Link>}
      />
    ),
  }),

  columnHelper.accessor('sourceDocument', {
    id: 'source',
    header: 'AFE Input Source',
    size: 180,
    enableSorting: true,
    meta: { type: 'enum' },
    cell: (info) => {
      const source = info.getValue();
      if (!source) return 'Manual';
      const label =
        info.row.original.afeNumber ??
        source.documentTitle ??
        `Document #${source.id}`;
      return (
        <Link to={source.href} title={label}>
          Auto-Connected
        </Link>
      );
    },
  }),

  columnHelper.accessor('wellType', {
    id: 'wellType',
    header: 'Well Type',
    size: 130,
    meta: { type: 'enum' },
    cell: (info) => {
      const value = info.getValue();
      return value ? (WELL_TYPE_LABELS[value] ?? value) : EMPTY_CELL;
    },
  }),

  columnHelper.accessor('afeType', {
    id: 'afeType',
    header: 'AFE Type',
    size: 180,
    meta: { type: 'enum' },
    cell: (info) => {
      const value = info.getValue();
      return value ? (AFE_TYPE_LABELS[value] ?? value) : EMPTY_CELL;
    },
  }),

  columnHelper.accessor('operator', {
    id: 'operator',
    header: 'Operator',
    size: 220,
    meta: { type: 'enum' },
    cell: (info) => (
      <LinkCell
        value={info.getValue()}
        href={info.row.original.operatorHref}
        showTitle
      />
    ),
  }),

  columnHelper.accessor('countyName', {
    id: 'countyName',
    header: 'County',
    size: 130,
    meta: { type: 'enum' },
  }),

  columnHelper.accessor('state', {
    id: 'state',
    header: 'State',
    size: 110,
    meta: { type: 'enum' },
  }),

  columnHelper.accessor('lateralLength', {
    id: 'lateralLength',
    header: 'Planned Lateral Length',
    size: 180,
    meta: { type: 'integer', format: { unit: ' ft' } },
  }),

  columnHelper.accessor('tvd', {
    id: 'tvd',
    header: 'Planned Total Depth',
    size: 170,
    meta: { type: 'integer', format: { unit: ' ft' } },
  }),

  columnHelper.accessor('basinName', {
    id: 'basinName',
    header: 'Basin',
    size: 130,
    meta: { type: 'enum' },
  }),

  columnHelper.accessor('reportedFormation', {
    id: 'reportedFormation',
    header: 'Reported Formation',
    size: 170,
    meta: { type: 'enum' },
  }),

  columnHelper.accessor('drillingCost', {
    id: 'drillingCost',
    header: 'Drilling Cost',
    size: 140,
    meta: { type: 'currency' },
  }),

  columnHelper.accessor('completionCost', {
    id: 'completionCost',
    header: 'Completion Cost',
    size: 150,
    meta: { type: 'currency' },
  }),

  columnHelper.accessor('facilitiesCost', {
    id: 'facilitiesCost',
    header: 'Facilities Cost',
    size: 140,
    meta: { type: 'currency' },
  }),

  columnHelper.accessor('contingenciesCost', {
    id: 'contingenciesCost',
    header: 'Contingencies Cost',
    size: 160,
    meta: { type: 'currency' },
  }),

  columnHelper.accessor('otherCost', {
    id: 'otherCost',
    header: 'Other Cost',
    size: 130,
    meta: { type: 'currency' },
  }),

  columnHelper.accessor('totalCost', {
    id: 'totalCost',
    header: 'Total Cost',
    size: 140,
    meta: { type: 'currency' },
  }),

  columnHelper.accessor('afeDate', {
    id: 'afeDate',
    header: 'AFE Date',
    size: 130,
    meta: { type: 'date', label: 'AFE Date' },
    cell: (info) => {
      const wellTypeFilter = info.table
        .getState()
        .columnFilters.find((f) => f.id === 'wellType')?.value as
        string[] | undefined;
      const isGeneric =
        wellTypeFilter?.length === 1 &&
        wellTypeFilter[0].toLowerCase().includes('generic');
      const tooltip = isGeneric
        ? 'Date created on the AFE document by the AFE Sender'
        : 'Date listed on the AFE document by the AFE Sender';
      return (
        <FloatingTooltip
          content={tooltip}
          placement="top"
          triggerClassName={styles.tooltipTrigger}
        >
          <span>{formatCellValue(info.getValue(), { type: 'date' })}</span>
        </FloatingTooltip>
      );
    },
  }),

  columnHelper.accessor('heldFlag', {
    id: 'heldFlag',
    header: 'Held Flag',
    size: 110,
  }),

  columnHelper.accessor('supportingFiles', {
    id: 'afeFileName',
    header: 'AFE File',
    size: 140,
    cell: (info) => {
      const supporting = info.getValue();
      const source = info.row.original.sourceDocument;

      // Combined file list: the source document PDF (linked by URL, no
      // duplicate DB row) goes first, followed by every SupportingFile::Afe
      // attached to the AfeHeader. Auto-connected rows always surface the
      // originating PDF.
      const combined: Array<{ key: string; label: string; href: string }> = [];

      if (source?.fileUrl) {
        combined.push({
          key: `src-${source.id}`,
          label: source.documentTitle ?? `Document #${source.id}`,
          href: source.fileUrl,
        });
      }

      supporting.forEach((f) => {
        if (!f.downloadUrl) return;
        combined.push({
          key: `sf-${f.id}`,
          label: f.fileName ?? `File #${f.id}`,
          href: f.downloadUrl,
        });
      });

      return (
        <CollapsedList
          items={combined}
          getLabel={(c) => c.label}
          renderItem={(c) => (
            <PlainLink href={c.href} target="_blank" rel="noopener noreferrer">
              {c.label}
            </PlainLink>
          )}
        />
      );
    },
  }),
];

/** Initial table state: pinned columns, default sort, and page size. */
export const initialTableState: InitialTableState = {
  columnPinning: {
    left: ['id', 'wellName', 'verificationState'],
  },
  sortColumn: 'id',
  sortDirection: 'desc',
  pageSize: 100,
};

// ─────────────────────────────────────
// Content Component
// ─────────────────────────────────────

/**
 * AFE Catalog content orchestrator. The DataTable owns page, pageSize, sort,
 * filter, and search state and fires `loadData` whenever any of them change.
 * `loadData` maps that state into API params and writes them to the search
 * atom, which triggers the fetch.
 * @return {JSX.Element} The AFE Catalog content.
 */
const AfeCatalogContent: FC = () => {
  const { searchAtom } = useMemo(() => createAfeCatalogSearchAtom(), []);
  const [searchResult, setSearchPayload] = useAtom(searchAtom);

  const [params, setParams] = useState<AfeCatalogParams>(DEFAULT_PARAMS);
  const [exportError, setExportError] = useState<string | null>(null);
  const [selectedUwi, setSelectedUwi] = useState<string | null>(null);
  const [isMapOpen, setIsMapOpen] = useState(true);

  const session = useAtomValue(sessionResourceAtom);
  const userId = session?.id;
  const exportAfeCatalog = useSetAtom(exportAfeCatalogAtom);

  const exportSubscriptionRef = useRef<ReturnType<typeof subscribe> | null>(
    null
  );

  useEffect(() => {
    return () => {
      if (exportSubscriptionRef.current) {
        exportSubscriptionRef.current.unbind('afe_catalog_export_complete');
        exportSubscriptionRef.current.unsubscribe();
        exportSubscriptionRef.current = null;
      }
    };
  }, []);

  // Params → atom sync: triggers API fetch when params change.
  useEffect(() => {
    setSearchPayload(() => params);
  }, [params, setSearchPayload]);

  // Server-side export: POST current filters/sort, then await the Pusher
  // `afe_catalog_export_complete` event on `user_<id>_exports` and open the
  // signed download URL when the job finishes.
  const handleServerExport = useCallback(async () => {
    if (!userId) {
      setExportError('Cannot start export: user session unavailable.');
      return;
    }
    setExportError(null);

    let exportId: string;
    try {
      ({ exportId } = await exportAfeCatalog(params));
    } catch (e) {
      setExportError(
        e instanceof Error
          ? e.message
          : 'Failed to start export. Please try again.'
      );
      return;
    }

    if (exportSubscriptionRef.current) {
      exportSubscriptionRef.current.unbind('afe_catalog_export_complete');
      exportSubscriptionRef.current.unsubscribe();
    }

    const subscription = subscribe(`user_${userId}_exports`);
    exportSubscriptionRef.current = subscription;

    subscription.bind(
      'afe_catalog_export_complete',
      (data: {
        export_id: string;
        status: 'complete' | 'failed';
        download_url?: string;
        error?: string;
      }) => {
        if (data.export_id !== exportId) return;

        subscription.unbind('afe_catalog_export_complete');
        subscription.unsubscribe();
        exportSubscriptionRef.current = null;

        if (data.status === 'complete' && data.download_url) {
          window.open(data.download_url, '_blank', 'noopener,noreferrer');
        } else {
          setExportError(data.error || 'Export failed. Please try again.');
        }
      }
    );
  }, [userId, params, exportAfeCatalog]);

  const exportPresets = useMemo(
    () => [
      {
        name: 'Export',
        format: 'xlsx' as const,
        onExport: handleServerExport,
      },
    ],
    [handleServerExport]
  );

  /**
   * Single source of truth for table-driven param updates. Wired to every
   * server-event callback (`onPaginationChange`, `onSortChange`, `onFilterChange`,
   * `onGlobalSearchChange`) so any change re-syncs the search params atom.
   * @param {TableState} state - Full table state snapshot from the DataTable.
   */
  const refetch = useCallback(
    ({ page, pageSize, sorting, globalFilter, columnFilters }: TableState) => {
      const sort = sorting[0];
      setParams({
        ...DEFAULT_PARAMS,
        page,
        pageSize,
        sortAttribute: sort?.id ?? 'id',
        sortDirection: sort?.desc === false ? 'ASC' : 'DESC',
        text: globalFilter || undefined,
        ...columnFiltersToParams(columnFilters),
      });
    },
    []
  );

  const rows = useMemo(
    () => flattenResults(searchResult.data?.results ?? []),
    [searchResult.data?.results]
  );

  return (
    <>
      <SummaryCards
        totalCount={searchResult.data?.totalCount}
        summary={searchResult.data?.aggregations}
      />
      {exportError && (
        <InlineNotification
          kind="error"
          title={exportError}
          lowContrast
          onCloseButtonClick={() => setExportError(null)}
        />
      )}
      <div className={styles.tableWrapper}>
        <DataTable<AfeRow>
          data={rows as AfeRow[]}
          columns={columns as DataTableColumnDef<AfeRow>[]}
          getRowId={(row) => String(row.id)}
          initialTableState={initialTableState}
          persistenceKey="afe-catalog-list-table"
          config={{
            columnFilters: false,
            export: { exportPresets },
          }}
          isLoading={searchResult.loading}
          error={searchResult.error ?? null}
          scroll={{
            mode: 'paginated',
            serverDriven: true,
            totalCount: searchResult.data?.totalCount ?? 0,
          }}
          onPaginationChange={refetch}
          onSortChange={refetch}
          onFilterChange={refetch}
          onGlobalSearchChange={refetch}
          onSelectionChange={(s) => {
            const id = s.selection.toggledNodes[0];
            if (!id) {
              setSelectedUwi(null);
              return;
            }
            const row = rows.find((r) => String(r.id) === id);
            setSelectedUwi(row?.slotId || null);
          }}
          leftToolbarActions={[
            <Button
              key="map-toggle"
              kind="ghost"
              size="md"
              hasIconOnly
              iconDescription={isMapOpen ? 'Hide map' : 'Show map'}
              renderIcon={MapBoundary}
              onClick={() => setIsMapOpen((open) => !open)}
            />,
          ]}
          betweenToolbarAndContent={
            isMapOpen ? <AfeCatalogMap uwi={selectedUwi} /> : undefined
          }
          renderFilterPanel={() => (
            <AfeCatalogFilters facets={searchResult.data?.facets} />
          )}
        />
      </div>
    </>
  );
};

export default AfeCatalogContent;
